import { useEffect, useState } from "react";
import { useAuth } from "@workos-inc/authkit-react";

/**
 * Manages the WorkOS access token for the Ano desktop app.
 *
 * Behaviour:
 * - On mount, fetches a token via AuthKit's `getAccessToken`.
 * - Schedules a refresh 5 minutes before the JWT's `exp`, falling back to
 *   a 5-minute cadence when the JWT cannot be parsed.
 * - On `visibilitychange` (visible) or `online`, refetches (force-refresh
 *   when coming back online) so the cache picks up any refresh that
 *   ZeroVisibilityRefresh performed in the background.
 * - Heartbeats every 10s; if the loop has been paused for >30s (e.g. the
 *   tab was throttled or asleep), refetches the token.
 * - Holds onto the last-known-good token across transient failures to
 *   avoid tearing down the sync engine unnecessarily.
 *
 * Returns `{ token, resolved }`. `resolved` flips to true after the first
 * fetch settles (success OR failure) so callers can distinguish "still
 * resolving" from "resolved with no token".
 */
export function useAccessToken() {
  const { getAccessToken } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let firstFetchResolved = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    let heartbeat: ReturnType<typeof setInterval> | null = null;
    let fetching = false;

    function setResolvedOnce() {
      if (!firstFetchResolved) {
        firstFetchResolved = true;
        setResolved(true);
      }
    }

    function scheduleRefresh(jwt: string) {
      try {
        const payload = JSON.parse(atob(jwt.split(".")[1]));
        if (payload.exp) {
          const expiresIn = payload.exp * 1000 - Date.now();
          const refreshIn = Math.max(expiresIn - 5 * 60 * 1000, 0);
          refreshTimer = setTimeout(() => fetchToken(), refreshIn);
          return;
        }
      } catch {
        // Fall through to default retry cadence below.
      }
      refreshTimer = setTimeout(() => fetchToken(), 5 * 60 * 1000);
    }

    async function fetchToken(forceRefresh = false) {
      if (fetching) return;
      fetching = true;

      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }

      const timeoutMs = 10000;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const timeoutPromise = new Promise<null>((resolve) => {
        timeoutId = setTimeout(() => resolve(null), timeoutMs);
      });

      const nextToken = await Promise.race([
        getAccessToken(forceRefresh ? { forceRefresh: true } : undefined).catch(
          () => null,
        ),
        timeoutPromise,
      ]);
      if (timeoutId) clearTimeout(timeoutId);

      fetching = false;
      if (!cancelled) {
        if (nextToken) {
          setToken(nextToken);
          scheduleRefresh(nextToken);
        } else {
          // Keep the last known-good token on transient failures to avoid
          // tearing down the sync engine unnecessarily.
          setToken((prev) => prev);
          refreshTimer = setTimeout(() => fetchToken(), 30_000);
        }
        setResolvedOnce();
      }
    }

    fetchToken().catch(() => {
      fetching = false;
      if (!cancelled) {
        refreshTimer = setTimeout(() => fetchToken(), 30_000);
        if (!firstFetchResolved) {
          setToken(null);
        }
        setResolvedOnce();
      }
    });

    function handleVisibilityChange() {
      // ZeroVisibilityRefresh owns the force-refresh on visibility return.
      // This handler just updates React state with whatever AuthKit currently
      // has cached (which may have been refreshed by ZeroVisibilityRefresh
      // already). Plain fetchToken() — no forceRefresh — to avoid double calls.
      if (document.visibilityState === "visible") {
        void fetchToken();
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    function handleOnline() {
      // Network came back — force-refresh in case the cached token expired
      // while we were offline.
      void fetchToken(true);
    }
    window.addEventListener("online", handleOnline);

    let lastHeartbeat = Date.now();
    heartbeat = setInterval(() => {
      const now = Date.now();
      if (now - lastHeartbeat > 30_000) {
        void fetchToken();
      }
      lastHeartbeat = now;
    }, 10_000);

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      if (heartbeat) clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
    };
  }, [getAccessToken]);

  return { token, resolved };
}
